import { Button, Checkbox, Input, Select, Textarea } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import { useModifierExposition, type ExpositionPresentee } from '../../../api/expositions.js';
import { useLieux } from '../../../api/lieux.js';
import { Field } from '../../Field.js';
import styles from './Tabs.module.css';

export interface InformationsTabProps {
  galerieSlug: string;
  exposition: ExpositionPresentee;
}

/**
 * Informations (S3-d) : texte bilingue, lieu ou stand, visibilité sur le
 * site, adresse (slug) — figée dès la première mise en ligne (§ 15.4).
 */
export function InformationsTab({ galerieSlug, exposition }: InformationsTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const lieux = useLieux(galerieSlug);
  const modifier = useModifierExposition(galerieSlug);

  const [texteFr, setTexteFr] = useState(exposition.texte?.fr ?? '');
  const [texteEn, setTexteEn] = useState(exposition.texte?.en ?? '');
  const [lieuId, setLieuId] = useState(exposition.lieuId ?? '');
  const [stand, setStand] = useState(exposition.stand ?? '');
  const [visibleSurLeSite, setVisibleSurLeSite] = useState(exposition.visibleSurLeSite);
  const [slug, setSlug] = useState(exposition.slug);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState(false);

  const slugFige = exposition.slugPublie !== null;
  const estFoire = exposition.type === 'foire';

  async function enregistrer(): Promise<void> {
    setErreur(null);
    setSucces(false);
    try {
      await modifier.mutateAsync({
        id: exposition.id,
        corps: {
          texte: { fr: texteFr, ...(texteEn ? { en: texteEn } : {}) },
          lieuId: lieuId || null,
          ...(estFoire ? { stand: stand || null } : {}),
          visibleSurLeSite,
          ...(!slugFige && slug !== exposition.slug ? { slug } : {}),
        },
      });
      setSucces(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
      <div className={styles.grille2}>
        <Field label={t('regie:expositions.informations.texteFr')} htmlFor="exposition-texte-fr">
          <Textarea
            id="exposition-texte-fr"
            rows={6}
            value={texteFr}
            onChange={(event) => setTexteFr(event.target.value)}
          />
        </Field>
        <Field label={t('regie:expositions.informations.texteEn')} htmlFor="exposition-texte-en">
          <Textarea
            id="exposition-texte-en"
            rows={6}
            value={texteEn}
            onChange={(event) => setTexteEn(event.target.value)}
          />
        </Field>
      </div>

      <div className={styles.grille2}>
        <Field label={t('regie:expositions.informations.lieuChamp')} htmlFor="exposition-lieu">
          <Select
            id="exposition-lieu"
            aria-label={t('regie:expositions.informations.lieuChamp')}
            value={lieuId}
            onValueChange={setLieuId}
            options={[
              { value: '', label: t('regie:expositions.informations.lieuAucun') },
              ...(lieux.data?.map((lieu) => ({ value: lieu.id, label: lieu.nom })) ?? []),
            ]}
          />
        </Field>
        {estFoire ? (
          <Field label={t('regie:expositions.informations.standChamp')} htmlFor="exposition-stand">
            <Input
              id="exposition-stand"
              value={stand}
              onChange={(event) => setStand(event.target.value)}
            />
          </Field>
        ) : null}
      </div>

      <Field label={t('regie:expositions.informations.slug')} htmlFor="exposition-slug">
        <Input
          id="exposition-slug"
          mono
          value={slug}
          disabled={slugFige}
          onChange={(event) => setSlug(event.target.value)}
        />
        {slugFige ? (
          <p className={styles.aide}>{t('regie:expositions.informations.slugFige')}</p>
        ) : null}
      </Field>

      {erreur ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
      {succes ? (
        <p className={styles.succes} role="status">
          {t('regie:expositions.informations.enregistre')}
        </p>
      ) : null}

      <div className={styles.pied}>
        <Checkbox
          id="exposition-visible"
          label={t('regie:expositions.informations.visibleSurLeSite')}
          checked={visibleSurLeSite}
          onCheckedChange={(valeur) => setVisibleSurLeSite(valeur === true)}
        />
        <Button onClick={() => void enregistrer()} loading={modifier.isPending}>
          {t('regie:expositions.informations.enregistrer')}
        </Button>
      </div>
    </form>
  );
}
